/**
 * Help & Resources Page
 *
 * Simple help page directing users to support email.
 * Minimal presence for beta phase.
 */

import { ArrowLeft, Mail, MessageCircle, FileText } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

export function Help(): JSX.Element {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-airy">
      {/* Navigation Header */}
      <div className="bg-white border-b border-soft-neutral">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="btn-ghost flex items-center gap-2 -ml-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-display text-4xl text-deep-forest font-light mb-4">
            Help & Resources
          </h1>
          <p className="text-muted text-lg">
            We're here to help you navigate your caregiving journey
          </p>
        </div>

        {/* Contact Card */}
        <div className="card mb-8">
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-evergreen" />
            </div>
            <h2 className="text-xl font-semibold text-ink mb-3">Contact Support</h2>
            <p className="text-muted mb-6 max-w-md mx-auto">
              Have questions, feedback, or need assistance? Our team is ready to help.
            </p>
            <a
              href="mailto:support@hesvara.com"
              className="btn-primary inline-flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              Email support@hesvara.com
            </a>
            <p className="text-sm text-muted mt-4">
              We typically respond within 1-2 business days
            </p>
          </div>
        </div>

        {/* Quick Links */}
        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-ink mb-4">Quick Links</h3>
            <div className="space-y-3">
              <Link
                to="/privacy"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-airy transition-colors"
              >
                <FileText className="w-5 h-5 text-muted" />
                <div>
                  <p className="font-medium text-ink">Privacy Policy</p>
                  <p className="text-sm text-muted">Learn how we protect your data</p>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Beta Notice */}
        <div className="mt-8 p-4 bg-brass/10 border border-brass/30 rounded-lg text-center">
          <p className="text-sm text-ink">
            <strong className="text-brass-text-warm">Beta Notice:</strong>{' '}
            Hesvara is currently in beta. We appreciate your patience as we continue
            to improve the platform based on your feedback.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Help;
